import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn, formatNumber, formatDate } from '@/lib/utils';

export function Transactions() {
  const { data, isLoading } = useQuery({
    queryKey: ['transactions'],
    queryFn: () => api.getTransactions(),
  });

  const transactions = data?.transactions || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Transactions</h2>
          <p className="text-sm text-muted-foreground">
            View and manage trade transactions
          </p>
        </div>
        <button className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">
          + New Transaction
        </button>
      </div>

      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Date</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Client</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Contract</th>
                <th className="px-6 py-3 text-center text-sm font-medium text-muted-foreground">Side</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-muted-foreground">Quantity</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-muted-foreground">Price</th>
                <th className="px-6 py-3 text-center text-sm font-medium text-muted-foreground">Source</th>
                <th className="px-6 py-3 text-center text-sm font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-muted-foreground">
                    Loading transactions...
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-muted-foreground">
                    No transactions found
                  </td>
                </tr>
              ) : (
                transactions.map((tx) => (
                  <tr key={tx.id} className="border-b last:border-0 hover:bg-muted/50">
                    <td className="px-6 py-4 text-sm">
                      {formatDate(tx.tradeDate)}
                    </td>
                    <td className="px-6 py-4 font-medium">
                      {tx.client?.name || tx.clientId}
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium">{tx.contract?.symbol}</p>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={cn(
                        'px-2 py-1 rounded text-xs font-medium',
                        tx.side === 'BUY' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      )}>
                        {tx.side}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-mono">
                      {formatNumber(tx.quantity)}
                    </td>
                    <td className="px-6 py-4 text-right font-mono">
                      ${tx.price.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="px-2 py-1 rounded bg-muted text-xs font-medium">
                        {tx.source}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={cn(
                        'px-2 py-1 rounded-full text-xs font-medium',
                        tx.status === 'CONFIRMED' && 'bg-green-100 text-green-700',
                        tx.status === 'PENDING' && 'bg-yellow-100 text-yellow-700',
                        tx.status === 'CANCELLED' && 'bg-gray-100 text-gray-700'
                      )}>
                        {tx.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
